import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./ActiveBuyerReport.scss";
import StickyHeader from "./components/StickyHeader";
import SectionNav from "./components/SectionNav";
import HeroSection from "./components/HeroSection";
import MatchPriorities from "./components/MatchPriorities";
import MatchPool from "./components/MatchPool";
import PropertyAlignment from "./components/PropertyAlignment";
import AgentNote from "./components/AgentNote";
import ZipOverview from "./components/ZipOverview";
import Methodology from "./components/Methodology";
import ContactCTA from "./components/ContactCTA";
import MobileCTA from "./components/MobileCTA";
import EditPanel from "./components/EditPanel";
import Toast from "./components/Toast";
import sampleData from "./__fixtures__/sampleData.json";
import { baseURL } from "../../constants/endPoints";
import { USER_INFO_KEY } from "../../constants/common";
import { getLocalStorage } from "../../helpers/common";

const MOBILE_NAV_BREAKPOINT = 1024;

const trimBase = (u) => (u || "").trim().replace(/\/+$/, "");

const shareAccessToken = () => {
  try {
    return getLocalStorage(USER_INFO_KEY)?.token?.accessToken || null;
  } catch (e) {
    return null;
  }
};

// Agent-side: mint a public, redacted, dead-end share token and return the
// /r/:token URL. Falls back to null on failure (caller uses current URL).
const mintShareUrl = async (inventoryId) => {
  if (!inventoryId) return null;
  try {
    const token = shareAccessToken();
    const headers = {
      "Content-Type": "application/json",
      "api-key": process.env.REACT_APP_REST_API_KEY || "",
    };
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(
      `${trimBase(baseURL)}/api/v2/active-buyer-report/${encodeURIComponent(inventoryId)}/share-token`,
      { headers }
    );
    if (!res.ok) return null;
    const body = await res.json();
    const shareToken = body?.result?.token;
    return shareToken ? `${window.location.origin}/r/${shareToken}` : null;
  } catch (e) {
    return null;
  }
};

// Pure resolver for the mount-time deep-link scroll: given a raw
// window.location.hash and a way to look up elements, decide what (if
// anything) should be scrolled to. Kept side-effect-free so it's testable
// without a real DOM/scroll environment.
export const resolveHashScrollTarget = (hash, getElementById) => {
  const id = String(hash || "").replace(/^#/, "");
  if (!id) return null;
  const element = getElementById(id);
  if (!element) return null;
  return { id, element };
};

const phoneToTel = (display, fallback) => {
  if (!display) return fallback;
  let digits = String(display).replace(/[^\d+]/g, "");
  if (!digits) return fallback;
  if (digits.charAt(0) !== "+" && digits.length === 10) digits = `+1${digits}`;
  return digits;
};

const storageKey = (base, slug) => `bls_active_buyer_${base}_${slug || "default"}`;

const safeLocalGet = (key) => {
  try {
    return typeof window !== "undefined" ? window.localStorage.getItem(key) : null;
  } catch (err) {
    return null;
  }
};

const safeLocalSet = (key, value) => {
  try {
    if (typeof window === "undefined") return;
    if (value == null || value === "") {
      window.localStorage.removeItem(key);
    } else {
      window.localStorage.setItem(key, value);
    }
  } catch (err) {
    /* noop */
  }
};

// The public /r/:token view must render only what the shared API endpoint
// returns - never an agent's locally-cached edit-panel overrides (which can
// include text that was never actually persisted server-side).
export const localOverride = (isShared, key) => (isShared ? null : safeLocalGet(key));

const ActiveBuyerReportView = ({ reportData, mode = "seller", inventoryId = null, isShared = false, onDownloadPdf = null }) => {
  const data = reportData || sampleData;

  const slug = (data && data.meta && data.meta.reportSlug) || "default";
  const noteKey = useMemo(() => storageKey("note", slug), [slug]);
  const noteTsKey = useMemo(() => storageKey("note_ts", slug), [slug]);
  const emailKey = useMemo(() => storageKey("email", slug), [slug]);
  const phoneKey = useMemo(() => storageKey("phone", slug), [slug]);
  const brokerageKey = useMemo(() => storageKey("brokerage", slug), [slug]);

  const defaultAgent = data.agent || {};
  const defaultPhoneTel = defaultAgent.phoneTel || phoneToTel(defaultAgent.phoneDisplay, "");

  const [noteState, setNoteState] = useState({
    text: localOverride(isShared, noteKey) || data.agentNote?.defaultText || "",
    ts: localOverride(isShared, noteTsKey) || data.agentNote?.savedTimestamp || ""
  });
  const [emailOverride, setEmailOverride] = useState(localOverride(isShared, emailKey) || "");
  const [phoneOverride, setPhoneOverride] = useState(localOverride(isShared, phoneKey) || "");
  const [brokerageOverride, setBrokerageOverride] = useState(localOverride(isShared, brokerageKey) || "");

  const agent = useMemo(() => {
    const email = emailOverride || defaultAgent.email;
    const phoneDisplay = phoneOverride || defaultAgent.phoneDisplay;
    const brokerage = brokerageOverride || defaultAgent.brokerage;
    return {
      ...defaultAgent,
      email,
      phoneDisplay,
      phoneTel: phoneToTel(phoneDisplay, defaultPhoneTel),
      brokerage
    };
  }, [defaultAgent, defaultPhoneTel, emailOverride, phoneOverride, brokerageOverride]);

  const [isNavOpen, setIsNavOpen] = useState(false);
  const [activeSectionId, setActiveSectionId] = useState("hero");
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastVisible, setToastVisible] = useState(false);
  const toastTimerRef = useRef(null);

  const isAgentMode = mode === "agent";

  const showToast = useCallback((msg) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToastMessage(msg);
    setToastVisible(true);
    toastTimerRef.current = setTimeout(() => setToastVisible(false), 2400);
  }, []);

  useEffect(() => () => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
  }, []);

  const isMobileLayout = useCallback(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(`(max-width: ${MOBILE_NAV_BREAKPOINT}px)`).matches;
  }, []);

  const closeNav = useCallback(() => {
    setIsNavOpen(false);
    if (typeof document !== "undefined") {
      document.body.classList.remove("abr-body-nav-lock");
    }
  }, []);

  const toggleNav = useCallback(() => {
    setIsNavOpen((prev) => {
      const next = !prev;
      if (typeof document !== "undefined") {
        if (next && isMobileLayout()) {
          document.body.classList.add("abr-body-nav-lock");
        } else {
          document.body.classList.remove("abr-body-nav-lock");
        }
      }
      return next;
    });
  }, [isMobileLayout]);

  useEffect(() => {
    const onResize = () => {
      if (typeof document !== "undefined" && !isMobileLayout()) {
        document.body.classList.remove("abr-body-nav-lock");
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [isMobileLayout]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && isNavOpen) closeNav();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isNavOpen, closeNav]);

  const navItems = useMemo(() => {
    const hasNote = !!(noteState.text && noteState.text.trim().length > 0);
    return [
      { id: "hero", label: "Overview" },
      { id: "priorities", label: "Match priorities" },
      { id: "pool", label: "Match pool" },
      { id: "alignment", label: "Property alignment" },
      { id: "agent-note", label: "Agent's note", hidden: !hasNote && !isAgentMode },
      { id: "overview", label: `${data.property?.zip || ""} overview`.trim() || "Zip overview" },
      { id: "methodology", label: "Methodology" },
      { id: "cta", label: `Contact ${agent.firstName || agent.fullName}` }
    ];
  }, [noteState.text, isAgentMode, data.property, agent]);

  useEffect(() => {
    // The browser's own on-load "scroll to #fragment" fires before this
    // report's async-loaded content exists (the parent shows a loading
    // skeleton until reportData resolves), so a deep link like
    // .../reports/active-buyer/1189#methodology lands with the hash set
    // but the viewport still at the top. Do it ourselves once mounted,
    // since by then the target sections are guaranteed to be in the DOM.
    if (typeof window === "undefined") return;
    const resolved = resolveHashScrollTarget(window.location.hash, (id) =>
      document.getElementById(id)
    );
    if (!resolved) return;
    resolved.element.scrollIntoView({ block: "start" });
    setActiveSectionId(resolved.id);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !("IntersectionObserver" in window)) return undefined;
    const ids = navItems.filter((item) => !item.hidden).map((item) => item.id);
    const elements = ids
      .map((id) => document.getElementById(id))
      .filter(Boolean);
    if (!elements.length) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSectionId(entry.target.id);
          }
        });
      },
      { rootMargin: "-30% 0px -55% 0px", threshold: 0 }
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [navItems]);

  const handleNavLinkClick = useCallback(
    (id, e) => {
      if (isMobileLayout()) {
        setTimeout(closeNav, 60);
      }
      setActiveSectionId(id);
    },
    [closeNav, isMobileLayout]
  );

  const handleShare = useCallback(async () => {
    if (typeof window === "undefined") return;
    // Agent shares a public, redacted dead-end link (/r/:token). A public
    // viewer just re-shares the link they're already on. Never share the
    // agent-gated /reports/active-buyer URL.
    let url = window.location.href;
    if (!isShared) {
      const shareUrl = await mintShareUrl(inventoryId);
      if (!shareUrl) {
        showToast("Couldn't create a shareable link, please try again");
        return;
      }
      url = shareUrl;
    }
    const titleText = `Active Buyer Report - ${data.property?.addressLine1 || ""}`;
    const text = `Real-time buyer demand for ${data.property?.addressLine1 || ""} in ${data.property?.zip || ""}.`;
    if (navigator.share) {
      navigator.share({ title: titleText, text, url }).catch(() => {
        /* user dismissed */
      });
    } else if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(
        () => showToast("Shareable link copied to clipboard"),
        () => {
          window.location.href = `mailto:?subject=${encodeURIComponent(titleText)}&body=${encodeURIComponent(`${text}\n\n${url}`)}`;
        }
      );
    } else {
      window.location.href = `mailto:?subject=${encodeURIComponent(titleText)}&body=${encodeURIComponent(`${text}\n\n${url}`)}`;
    }
  }, [isShared, inventoryId, data.property, showToast]);

  const printFallback = useCallback(() => {
    if (typeof window === "undefined") return;
    const printTipKey = "abr_print_tip_seen";
    let seen = false;
    try {
      seen = window.localStorage.getItem(printTipKey) === "1";
    } catch (err) {
      seen = false;
    }
    if (!seen) {
      showToast('Tip: uncheck "Headers and footers" in print options for a cleaner PDF');
      try {
        window.localStorage.setItem(printTipKey, "1");
      } catch (err) {
        /* noop */
      }
      setTimeout(() => window.print(), 1200);
    } else {
      window.print();
    }
  }, [showToast]);

  const pdfBusyRef = useRef(false);
  const [isPdfBusy, setIsPdfBusy] = useState(false);
  const handlePrint = useCallback(async () => {
    // inventoryId is only present on the agent-gated route; the shared
    // /r/:token route's onDownloadPdf closes over its own token instead, so
    // requiring inventoryId here would always fall through to printFallback
    // for shared viewers even when a real PDF endpoint is available.
    if (typeof onDownloadPdf !== "function") {
      printFallback();
      return;
    }
    if (pdfBusyRef.current) return;
    pdfBusyRef.current = true;
    setIsPdfBusy(true);
    showToast("Preparing your report PDF - this takes a few seconds...");
    try {
      await onDownloadPdf(inventoryId);
      showToast("Report PDF downloaded");
    } catch (err) {
      showToast("PDF download failed, opening print view instead");
      printFallback();
    } finally {
      pdfBusyRef.current = false;
      setIsPdfBusy(false);
    }
  }, [onDownloadPdf, inventoryId, printFallback, showToast]);

  const applyCustomization = useCallback(
    (payload, { persistLocal = true } = {}) => {
      const ts = new Date().toISOString();
      setNoteState({ text: payload.note || "", ts: payload.note ? ts : "" });
      setEmailOverride(payload.email || "");
      setPhoneOverride(payload.phone || "");
      setBrokerageOverride(payload.brokerage || "");
      if (persistLocal) {
        safeLocalSet(noteKey, payload.note || "");
        safeLocalSet(noteTsKey, payload.note ? ts : "");
        safeLocalSet(emailKey, payload.email || "");
        safeLocalSet(phoneKey, payload.phone || "");
        safeLocalSet(brokerageKey, payload.brokerage || "");
      }
    },
    [noteKey, noteTsKey, emailKey, phoneKey, brokerageKey]
  );

  const handleSaveEdit = useCallback(
    (payload) => {
      applyCustomization(payload);
      setIsEditOpen(false);
      showToast("Customizations saved");
    },
    [applyCustomization, showToast]
  );

  const handleRemoteHydrate = useCallback(
    (remote) => {
      if (!remote) return;
      const hasAny =
        (remote.note && remote.note.trim().length) ||
        (remote.email && remote.email.trim().length) ||
        (remote.phone && remote.phone.trim().length) ||
        (remote.brokerage && remote.brokerage.trim().length);
      if (!hasAny) return;
      applyCustomization({
        note: remote.note || "",
        email: remote.email || "",
        phone: remote.phone || "",
        brokerage: remote.brokerage || ""
      });
    },
    [applyCustomization]
  );

  const initialEditValues = useMemo(
    () => ({
      note: noteState.text,
      email: agent.email || "",
      phone: agent.phoneDisplay || "",
      brokerage: agent.brokerage || ""
    }),
    [noteState.text, agent.email, agent.phoneDisplay, agent.brokerage]
  );

  const rootClass = [
    "abr-root",
    isNavOpen ? "is-nav-open" : "",
    isMobileLayout() && isNavOpen ? "is-nav-open-lock" : "",
    "has-mobile-cta"
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={rootClass}>
      <a href="#priorities" className="abr-skip-link">Skip to report content</a>

      <StickyHeader
        agent={agent}
        property={data.property}
        isNavOpen={isNavOpen}
        onToggleNav={toggleNav}
        onShare={handleShare}
        onPrint={handlePrint}
        isPdfBusy={isPdfBusy}
      />

      <SectionNav
        isOpen={isNavOpen}
        items={navItems}
        activeId={activeSectionId}
        onLinkClick={handleNavLinkClick}
      />

      <div className="abr-nav-backdrop" aria-hidden="true" onClick={closeNav} />

      <div className="abr-page-shell">
        <main id="abr-main">
          <HeroSection
            generatedAtDisplay={data.meta?.generatedAtDisplay}
            property={data.property}
            hero={data.hero}
            agent={agent}
          />
          <MatchPriorities priorities={data.priorities} property={data.property} agent={agent} />
          <MatchPool pool={data.pool} property={data.property} agent={agent} matches={data.matches} />
          <PropertyAlignment alignment={data.alignment} agent={agent} />
          <AgentNote
            agent={agent}
            note={noteState}
            mode={mode}
            placeholderText={data.agentNote?.placeholderText}
          />
          <ZipOverview zipOverview={data.zipOverview} property={data.property} agent={agent} />
          <Methodology methodology={data.methodology} agent={agent} />
          <ContactCTA agent={agent} property={data.property} />
        </main>
      </div>

      <footer className="abr-footer" role="contentinfo">
        <span className="abr-brand">
          <span className="abr-brand-dot" aria-hidden="true" />
          The Buyer Listing Service<span className="abr-brand-reg">&reg;</span>
        </span>
        <span className="abr-footer-small">
          <strong>
            {agent.fullName} &middot; {agent.brokerage} &middot; {agent.licenseLabel} &middot; {agent.mlsLabel}
          </strong>
          <br />
          Active Buyer Report &middot; Generated {data.meta?.generatedAtDisplay}. The Buyer Listing Service
          <span className="abr-brand-reg">&reg;</span> is a registered trademark. Data is believed reliable but not
          guaranteed.
        </span>
        <span className="abr-footer-tools">
          <a href="#hero" onClick={handlePrint}>Print-ready PDF version</a>
        </span>
      </footer>

      <MobileCTA agent={agent} />

      {isAgentMode ? (
        <button
          className="abr-edit-fab"
          type="button"
          aria-haspopup="dialog"
          aria-controls="abr-edit-panel"
          onClick={() => setIsEditOpen(true)}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 20h9" />
            <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
          </svg>
          <span>Customize report</span>
        </button>
      ) : null}

      {isAgentMode ? (
        <EditPanel
          isOpen={isEditOpen}
          initialValues={initialEditValues}
          recipientName={null}
          inventoryId={inventoryId}
          onClose={() => setIsEditOpen(false)}
          onSave={handleSaveEdit}
          onRemoteLoaded={handleRemoteHydrate}
          onToast={showToast}
        />
      ) : null}

      <Toast message={toastMessage} visible={toastVisible} />
    </div>
  );
};

export default ActiveBuyerReportView;
